/**
 * DemoStore — try.veldon.cz is the REAL Veldon CMS running in browser-only
 * mode. It does NOT re-implement editing: every edit flows through the SAME
 * engine operations + SAME validation the server uses, and the ONLY difference
 * from the server is that "persist" means commit to the in-memory snapshot +
 * localStorage instead of saveDraft/DB.
 *
 * Two real dispatchers are the entire mutation surface:
 *   - applyOperation(instanceId, opName, input) mirrors
 *     content-service.applyModuleOperation EXACTLY:
 *       resolveModuleData(defaultRegistry, mount, stored)
 *       -> module.operations[opName].input.safeParse(input)
 *       -> operation.apply(resolved.data, parsedInput)   (the REAL pure op)
 *       -> validateSnapshotAgainstDefinition(candidate)  (the REAL policy)
 *       -> commit + save + re-render.
 *     This is the ONLY way module data (menu, hours, contact copy) changes.
 *   - setContentValue(sectionId, fieldId, value) is genuine CMS content editing:
 *     it sets snapshot.values[sectionId][fieldId] and validates the candidate
 *     with the SAME validateSnapshotAgainstDefinition (values are typed by each
 *     section's editableManifest, so an invalid edit is rejected exactly as
 *     content.PUT would). Inline text + image + the gallery `list` field all go
 *     through here.
 *
 * A fabricated capability is therefore IMPOSSIBLE: if there is no real engine
 * operation / manifest field behind an edit, the demo cannot offer it (there is
 * no theme/accent/palette operation in the engine, so no such control exists).
 *
 * The engine (`renderResolvedSite` + the module registry + the validators) is
 * NOT forked — it is imported through the `./engine.js` seam. Undo/redo,
 * localStorage persistence and the share codec are legitimate browser-only-mode
 * mechanics (that IS how the demo persists without a DB).
 */
import {
  ContentSnapshot,
  ContentValidationError,
  defaultRegistry,
  makeModuleDataValidator,
  ModuleOpError,
  renderResolvedSite,
  resolveModuleData,
  validateSnapshotAgainstDefinition,
  VenueMenuHoursData,
  type ContentSnapshotT,
  type DefModuleMountT,
  type FieldValueT,
  type JsonValueT,
  type SiteDefinitionT,
  type VenueMenuHoursDataT,
} from './engine.js';

export interface DemoFixture {
  definition: SiteDefinitionT;
  snapshot: ContentSnapshotT;
}

export interface MenuItemFieldsPatch {
  name?: string;
  description?: string;
  price?: string;
}

const HISTORY_CAP = 60;
const STORAGE_KEY = 'veldon-try:v1';
const SHARE_HASH_MAX_LENGTH = 30_000;

interface PersistedState {
  snapshot: ContentSnapshotT;
}

/** Find an editable-manifest entry by (sectionId, fieldId) in a pattern or
 *  freeform section (module mounts carry no editableManifest of their own).
 *  Used only to pick the value SHAPE for a text/richtext inline edit; the
 *  authoritative accept/reject is the engine validator in setContentValue. */
function findManifestEntry(
  definition: SiteDefinitionT,
  sectionId: string,
  fieldId: string,
): { type: string } | undefined {
  for (const page of definition.pages) {
    for (const section of page.sections) {
      if (section.id !== sectionId || section.kind === 'module') continue;
      const entry = section.editableManifest.find((candidate) => candidate.fieldId === fieldId);
      if (entry) return entry;
    }
  }
  return undefined;
}

/** Locate the module mount for an instanceId across every page's sections
 *  (module mounts are addressed by instanceId, not by section id) — the same
 *  lookup content-service.findModuleMount performs. */
function findModuleMount(definition: SiteDefinitionT, instanceId: string): DefModuleMountT | undefined {
  for (const page of definition.pages) {
    for (const section of page.sections) {
      if (section.kind === 'module' && section.instanceId === instanceId) return section;
    }
  }
  return undefined;
}

const textEncoder = new TextEncoder();
const textDecoder = new TextDecoder();

type Bytes = Uint8Array<ArrayBuffer>;

function bytesToBase64Url(bytes: Bytes): string {
  let binary = '';
  const chunkSize = 0x8000;
  for (let offset = 0; offset < bytes.length; offset += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(offset, offset + chunkSize));
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function base64UrlToBytes(value: string): Bytes {
  const padded = value.replace(/-/g, '+').replace(/_/g, '/');
  const base64 = padded + '='.repeat((4 - (padded.length % 4)) % 4);
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
  return bytes;
}

async function compressToBytes(json: string): Promise<{ format: 'g' | 'r'; bytes: Bytes }> {
  const input = textEncoder.encode(json);
  if (typeof CompressionStream === 'undefined') return { format: 'r', bytes: input };
  try {
    const stream = new Blob([input]).stream().pipeThrough(new CompressionStream('gzip'));
    const compressed = new Uint8Array(await new Response(stream).arrayBuffer());
    return { format: 'g', bytes: compressed };
  } catch {
    return { format: 'r', bytes: input };
  }
}

async function decompressFromBytes(format: 'g' | 'r', bytes: Bytes): Promise<string> {
  if (format === 'r') return textDecoder.decode(bytes);
  const stream = new Blob([bytes]).stream().pipeThrough(new DecompressionStream('gzip'));
  const decompressed = new Uint8Array(await new Response(stream).arrayBuffer());
  return textDecoder.decode(decompressed);
}

async function encodeSnapshotToHash(snapshot: ContentSnapshotT): Promise<string | null> {
  const { format, bytes } = await compressToBytes(JSON.stringify(snapshot));
  const encoded = `${format}${bytesToBase64Url(bytes)}`;
  return encoded.length > SHARE_HASH_MAX_LENGTH ? null : encoded;
}

async function decodeSnapshotFromHash(hash: string): Promise<ContentSnapshotT | null> {
  const clean = hash.startsWith('#') ? hash.slice(1) : hash;
  const withoutKey = clean.startsWith('s=') ? clean.slice(2) : clean;
  const format = withoutKey[0];
  if (format !== 'g' && format !== 'r') return null;
  try {
    const json = await decompressFromBytes(format, base64UrlToBytes(withoutKey.slice(1)));
    const parsed: unknown = JSON.parse(json);
    const result = ContentSnapshot.safeParse(parsed);
    return result.success ? result.data : null;
  } catch {
    return null;
  }
}

export class DemoStore {
  private readonly definition: SiteDefinitionT;
  private snapshot: ContentSnapshotT;
  private readonly initialSnapshot: ContentSnapshotT;
  private history: ContentSnapshotT[] = [];
  private future: ContentSnapshotT[] = [];
  private readonly listeners = new Set<() => void>();

  constructor(fixture: DemoFixture) {
    this.definition = fixture.definition;
    this.snapshot = structuredClone(fixture.snapshot);
    this.initialSnapshot = structuredClone(fixture.snapshot);
  }

  // ---- read access -------------------------------------------------------

  get canUndo(): boolean {
    return this.history.length > 0;
  }

  get canRedo(): boolean {
    return this.future.length > 0;
  }

  getDefinition(): SiteDefinitionT {
    return this.definition;
  }

  /** Defensive clone — callers can inspect freely without risking a mutation
   *  that bypasses the dispatchers/history/notify(). */
  getSnapshot(): ContentSnapshotT {
    return structuredClone(this.snapshot);
  }

  /** Typed read of the venue.menu-hours module data for a mount instance. */
  getVenueData(instanceId: string): VenueMenuHoursDataT {
    const entry = this.snapshot.modules[instanceId];
    if (!entry) throw new Error(`no module data for instance "${instanceId}"`);
    return VenueMenuHoursData.parse(structuredClone(entry.data));
  }

  /** Read a snapshot list value (e.g. the gallery `galerie-images` field); []
   *  if unset. This is a real editableManifest `list` field, so it is genuine
   *  CMS content read/written through setContentValue. */
  getListField(sectionId: string, fieldId: string): JsonValueT[] {
    const value = this.snapshot.values[sectionId]?.[fieldId];
    return value && value.type === 'list' ? structuredClone(value.value) : [];
  }

  /**
   * Render the current (definition, snapshot) pair through the REAL engine and
   * return the raw `/index.html`. Never throws: a validation or render failure
   * comes back as a plain error string so a caller can always surface it.
   */
  renderContent(): string {
    try {
      const { files } = renderResolvedSite(this.definition, this.snapshot);
      const html = files['/index.html'];
      return html ?? 'RENDER ERROR: no "/index.html" in the rendered output';
    } catch (error) {
      if (error instanceof ContentValidationError) {
        const details = error.issues.map((issue) => `  - ${issue.where}: ${issue.message}`).join('\n');
        return `RENDER ERROR (validation failed):\n${details}`;
      }
      return `RENDER ERROR: ${error instanceof Error ? error.message : String(error)}`;
    }
  }

  // ---- mutation plumbing --------------------------------------------------

  private notify(): void {
    for (const listener of this.listeners) listener();
  }

  /**
   * The SINGLE commit point every edit reaches AFTER it has produced a
   * validated candidate snapshot: push the current snapshot onto history, swap
   * in the candidate, drop the redo stack, notify. A rejected edit throws
   * BEFORE this is ever called, so state + history stay pristine.
   */
  private commitSnapshot(candidate: ContentSnapshotT): void {
    this.history.push(this.snapshot);
    if (this.history.length > HISTORY_CAP) this.history.shift();
    this.future = [];
    this.snapshot = candidate;
    this.notify();
  }

  /**
   * The REAL policy gate — the exact call saveDraft makes: validate the whole
   * candidate snapshot against the definition, with module payloads checked by
   * the registry-backed makeModuleDataValidator. Throws the engine's own first
   * issue on failure, so the demo enforces precisely the rules the CMS does.
   */
  private assertValid(candidate: ContentSnapshotT): void {
    const issues = validateSnapshotAgainstDefinition(
      this.definition,
      candidate,
      makeModuleDataValidator(defaultRegistry),
    );
    const first = issues[0];
    if (first) throw new Error(`${first.where}: ${first.message}`);
  }

  // ---- dispatcher 1: real module operations ------------------------------

  /**
   * Apply one module operation to a mount's stored data — a 1:1 mirror of
   * content-service.applyModuleOperation, differing ONLY in that the validated
   * result is committed to the in-memory snapshot + localStorage instead of
   * saveDraft/DB. This is the ONLY way module data changes. A bad op name,
   * invalid input, or a ModuleOpError from the pure op surfaces as a clean
   * Error the UI can show; nothing mutates unless the candidate validates.
   */
  applyOperation(instanceId: string, operationName: string, input: unknown): void {
    const mount = findModuleMount(this.definition, instanceId);
    if (!mount) throw new Error(`unknown module instance "${instanceId}"`);

    const module = defaultRegistry.get(mount.moduleId);
    if (!module) throw new Error(`unknown module "${mount.moduleId}"`);

    if (!Object.hasOwn(module.operations, operationName)) {
      throw new Error(`unknown operation "${operationName}" on ${mount.moduleId}`);
    }
    const operation = module.operations[operationName]!;

    // Own-property lookup (never reach through the prototype), matching the
    // defensive convention content-service uses.
    const stored = Object.hasOwn(this.snapshot.modules, instanceId) ? this.snapshot.modules[instanceId] : undefined;
    const resolved = resolveModuleData(defaultRegistry, mount, stored);
    if ('code' in resolved) throw new Error(`${resolved.code}: ${resolved.message}`);

    const parsedInput = operation.input.safeParse(input);
    if (!parsedInput.success) {
      throw new Error(parsedInput.error.issues[0]?.message ?? 'invalid operation input');
    }

    let newData: JsonValueT;
    try {
      newData = operation.apply(resolved.data, parsedInput.data) as JsonValueT;
    } catch (error) {
      if (error instanceof ModuleOpError) throw new Error(error.message);
      throw error;
    }

    const candidate: ContentSnapshotT = {
      ...this.snapshot,
      modules: { ...this.snapshot.modules, [instanceId]: { dataVersion: module.dataVersion, data: newData } },
    };
    this.assertValid(candidate);
    this.commitSnapshot(candidate);
  }

  // ---- dispatcher 2: real content values ---------------------------------

  /**
   * Set one typed content value (the REAL content model). The candidate
   * snapshot is validated with the SAME validateSnapshotAgainstDefinition the
   * server's content.PUT uses, so a value that doesn't bind to a manifest entry
   * of the matching type, violates a constraint (e.g. gallery maxItems), or
   * fails the URL/richtext policy is rejected exactly as it would be server-side.
   */
  setContentValue(sectionId: string, fieldId: string, value: FieldValueT): void {
    const candidate: ContentSnapshotT = {
      ...this.snapshot,
      values: {
        ...this.snapshot.values,
        [sectionId]: { ...(this.snapshot.values[sectionId] ?? {}), [fieldId]: value },
      },
    };
    this.assertValid(candidate);
    this.commitSnapshot(candidate);
  }

  // ---- thin typed wrappers over the two dispatchers ----------------------
  // These add NO edit logic — they only shape the input for a real dispatcher.

  /** Inline text/richtext edit. The manifest picks the value shape; the engine
   *  validator is the authority on whether the edit is accepted. */
  setFieldValue(sectionId: string, fieldId: string, value: string): void {
    const entry = findManifestEntry(this.definition, sectionId, fieldId);
    if (!entry || (entry.type !== 'text' && entry.type !== 'richtext')) {
      throw new Error(`setFieldValue: "${sectionId}/${fieldId}" is not an editable text/richtext field`);
    }
    this.setContentValue(sectionId, fieldId, entry.type === 'richtext' ? { type: 'richtext', value } : { type: 'text', value });
  }

  /** Image-field swap (an ImageValue content field). */
  setImage(sectionId: string, fieldId: string, src: string, alt: string): void {
    this.setContentValue(sectionId, fieldId, { type: 'image', value: { src, alt } });
  }

  /** Replace a snapshot `list` value wholesale (the gallery add/remove/reorder
   *  are computed by the caller and committed as one new array = one history
   *  step, validated against the field's constraints). */
  setListField(sectionId: string, fieldId: string, value: JsonValueT[]): void {
    this.setContentValue(sectionId, fieldId, { type: 'list', value });
  }

  // venue.menu-hours — real operation names (see venueMenuHours.operations).
  addMenuItem(instanceId: string, categoryId: string, name: string, price: string, description?: string): void {
    this.applyOperation(instanceId, 'addItem', {
      categoryId,
      name,
      price,
      ...(description !== undefined && description !== '' ? { description } : {}),
    });
  }

  updateMenuItem(instanceId: string, categoryId: string, itemId: string, fields: MenuItemFieldsPatch): void {
    this.applyOperation(instanceId, 'updateItem', { categoryId, itemId, fields });
  }

  removeMenuItem(instanceId: string, categoryId: string, itemId: string): void {
    this.applyOperation(instanceId, 'deleteItem', { categoryId, itemId });
  }

  reorderMenuItems(instanceId: string, categoryId: string, orderedIds: string[]): void {
    this.applyOperation(instanceId, 'reorderItems', { categoryId, orderedIds });
  }

  setHours(instanceId: string, rules: VenueMenuHoursDataT['hours']['regular']): void {
    this.applyOperation(instanceId, 'setRegularHours', { rules });
  }

  // forms.contact — the single real operation is `updateCopy`.
  updateContactCopy(instanceId: string, fields: Record<string, string>): void {
    this.applyOperation(instanceId, 'updateCopy', { fields });
  }

  // ---- undo / redo --------------------------------------------------------

  undo(): void {
    const previous = this.history.pop();
    if (previous === undefined) return;
    this.future.push(this.snapshot);
    if (this.future.length > HISTORY_CAP) this.future.shift();
    this.snapshot = previous;
    this.notify();
  }

  redo(): void {
    const next = this.future.pop();
    if (next === undefined) return;
    this.history.push(this.snapshot);
    if (this.history.length > HISTORY_CAP) this.history.shift();
    this.snapshot = next;
    this.notify();
  }

  // ---- persistence (localStorage — the browser-only-mode "DB") ------------

  save(): void {
    const payload: PersistedState = { snapshot: this.snapshot };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  }

  /** Returns whether a persisted state was found and restored. */
  load(): boolean {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return false;
    try {
      const parsed: unknown = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') return false;
      const candidate = parsed as Partial<PersistedState>;
      const parsedSnapshot = ContentSnapshot.safeParse(candidate.snapshot);
      if (!parsedSnapshot.success) return false;
      this.snapshot = this.healMissingModules(parsedSnapshot.data);
      this.history = [];
      this.future = [];
      this.notify();
      return true;
    } catch {
      return false;
    }
  }

  /** Replace the current snapshot wholesale (share-hash hydration). Same
   *  invariants as load(): history reset, schema-rechecked defensively. */
  hydrate(snapshot: ContentSnapshotT): void {
    const parsed = ContentSnapshot.safeParse(snapshot);
    if (!parsed.success) throw new Error('hydrate: snapshot failed schema validation');
    this.snapshot = this.healMissingModules(parsed.data);
    this.history = [];
    this.future = [];
    this.notify();
  }

  /** Forward-heal a snapshot saved BEFORE a module mount was added to the
   *  venue definition (a pre-events/pre-shop localStorage save, an old edition
   *  rolled back to, an old share hash): the definition requires module data
   *  for every mount, so one missing entry would replace the whole preview
   *  with a render error. Seed absent entries from the pristine fixture;
   *  entries the visitor has edited are untouched. */
  private healMissingModules(snapshot: ContentSnapshotT): ContentSnapshotT {
    const missing = Object.keys(this.initialSnapshot.modules).filter((id) => !(id in snapshot.modules));
    if (missing.length === 0) return snapshot;
    const healed = structuredClone(snapshot);
    for (const id of missing) healed.modules[id] = structuredClone(this.initialSnapshot.modules[id]!);
    return healed;
  }

  /** Back to the original fixture — clears history and any saved state. */
  reset(): void {
    this.snapshot = structuredClone(this.initialSnapshot);
    this.history = [];
    this.future = [];
    localStorage.removeItem(STORAGE_KEY);
    this.notify();
  }

  // ---- share link (zero backend) -----------------------------------------

  /** null when the encoded payload would exceed the ~30k char budget a URL
   *  hash can carry — the caller offers a file export instead. */
  async toShareHash(): Promise<string | null> {
    return encodeSnapshotToHash(this.snapshot);
  }

  /** Hydrates a snapshot from a `location.hash`-shaped string (leading '#' and
   *  an optional `s=` key tolerated). Returns null on anything
   *  malformed/tampered/schema-invalid. */
  static async fromShareHash(hash: string): Promise<ContentSnapshotT | null> {
    return decodeSnapshotFromHash(hash);
  }

  // ---- change notification ------------------------------------------------

  /** Fires after every mutation (edits, undo/redo, load, reset). Returns an
   *  unsubscribe function. The store never auto-saves — a caller wires
   *  `onChange(() => store.save())` if it wants that. */
  onChange(callback: () => void): () => void {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }
}
